import './styles.scss'

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becQuantity from '@/components/B2becQuantity'
import B2becTranslation from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import FormatPrice from '@/components/FormatPrice'
import { InfoIcon } from '@/components/Icons'
import { rentalService } from '@/services/fapi'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectIsAddingToCartFromPriceBox,
  selectShouldDisplayDeliveryDates,
} from '@/store/selectors/cartSelector'
import {
  selectHideEstimatedDeliveryTime,
  selectHideListPriceGlobal,
} from '@/store/selectors/configSelector'
import {
  selectShouldDisplayAddToCart,
  selectShowRequiresInstruction,
} from '@/store/selectors/productSelector'
import { selectUserEmail } from '@/store/slices/authenticationSlice'
import { addProductToCart } from '@/store/slices/cartSlice'
import { selectCustomerName, selectCustomerPhone } from '@/store/slices/customerSlice'
import { useAsync } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import { ASYNC_STATUS } from '@/libs/constants'
import {
  MODULE_CART_CHECKOUT_RIGHTS,
  MODULE_SERVICE_RIGHTS,
  MODULE_SHOP_NAVIGATION_RIGHTS,
} from '@/libs/constants/modulerights'
import { formatDate, getFormatType } from '@/libs/utils/dateUtils'

import RentalModal from '../../RentalPage/RentalModal'
import { useEnquiryModal } from '../EnquiryModal/useEnquiryModal'

interface AddToCartButtonOptions {
  isShowEnquiryButton?: boolean
  shouldDisplayAddToCart?: boolean
  shouldDisableEnquiry?: boolean
}

interface PriceBoxProps {
  isLoading?: boolean
  isAvailable?: boolean
  materialNumber?: string
  currencyUnit?: string
  listingPrice?: number
  price?: number
  deliveryDates?: {
    quantity?: number
    materialNumber?: string
    deliveryDate?: string
  }[]
  requiresInstruction?: boolean
  isBProgramProduct?: boolean
  materialName?: string
  isDangerous?: boolean
  hasMinQuantity?: boolean
  shouldDisplayListPrice?: boolean
}

const emptyDeliveryDates = []

function PriceBox(props: PriceBoxProps) {
  const {
    isLoading = false,
    isAvailable = false,
    materialNumber = '',
    currencyUnit = '',
    listingPrice = 0,
    price = 0,
    deliveryDates = emptyDeliveryDates,
    isBProgramProduct = false,
    requiresInstruction = false,
    materialName = '',
    isDangerous = false,
    hasMinQuantity = false,
    shouldDisplayListPrice = false,
  } = props

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const shouldDisplayAddToCart = useAppSelector(selectShouldDisplayAddToCart)
  const isAddingToCart = useAppSelector(selectIsAddingToCartFromPriceBox)
  const companyName = useAppSelector(selectCustomerName)
  const phoneNumber = useAppSelector(selectCustomerPhone)
  const email = useAppSelector(selectUserEmail)
  const shouldDisplayDeliveryDates = useAppSelector(
    selectShouldDisplayDeliveryDates,
  )
  const shouldHideListPriceGlobally = useAppSelector(selectHideListPriceGlobal)
  const showRequiresInstructionInfo = useAppSelector(
    selectShowRequiresInstruction,
  )
  const hideEstimatedDeliveryTimeGlobally = useAppSelector(selectHideEstimatedDeliveryTime)
  const hideEstimatedDeliveryTime = hideEstimatedDeliveryTimeGlobally || !shouldDisplayDeliveryDates

  const {
    verifiedPermissions: [
      hasAddProductToCartPermission,
      hasCreateProductServicePermission,
    ],
  } = usePermission([
    MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_TO_SHOPPING_CART,
    MODULE_SERVICE_RIGHTS.CREATE_PRODUCT_REQUEST,
  ])

  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  const [quantity, setQuantity] = useState(1)
  const onChangeQuantity = (newQuantity) => setQuantity(newQuantity)

  const onAddProductToCart = () => {
    dispatch(addProductToCart({ materialNumber, quantity }))
  }
  const onFindAlternatives = () => {
    // TODO: implement on button click
  }

  const getIsRentable = useCallback(async () => {
    const response = await rentalService.getIsRentable(materialNumber)
    return response?.data
  }, [materialNumber])

  const { value: isRentable, status: isRentableLoading } = useAsync(
    getIsRentable,
    true,
  )

  const { showEnquiryModal, EnquiryModal } = useEnquiryModal({
    materialNumber,
    materialName,
    companyName,
    phoneNumber,
    email,
  })

  const renderAddToCartButton = (options: AddToCartButtonOptions = {}) => {
    if (options.isShowEnquiryButton) {
      return (
        <CustomButton
          className="price-box__bottom__button"
          disabled={!materialNumber || options.shouldDisableEnquiry}
          type="default"
          onClick={showEnquiryModal}
        >
          {t('productDetails.priceBox.buttonEnquiry')}
        </CustomButton>
      )
    }

    return (
      <CustomButton
        className="price-box__bottom__button"
        disabled={isAddingToCart || !materialNumber}
        loading={isAddingToCart}
        onClick={onAddProductToCart}
        type="default"
      >
        {t('productDetails.priceBox.button')}
      </CustomButton>
    )
  }

  const renderRentableButton = () => {
    if (isRentable) {
      return isLoading ? <Skeleton height={50} /> : (
        <>
          <Divider style={{ margin: '10px 0' }} />
          <RentalModal deviceName={materialName}>
            <CustomButton style={{ width: '100%' }} type="ghost">
              {t('productDetails.priceBox.requestRental')}
            </CustomButton>
          </RentalModal>
        </>
      )
    }
    return null
  }

  const renderRentalInfo = () => {
    if (isRentable) {
      return isLoading ? <Skeleton height={20} /> : (
        <div className="price-box__delivery-info">
          {t('productDetails.priceBox.rentalInfo')}
        </div>
      )
    }
    return null
  }

  const renderListingPrice = (lp, options = {}) => {
    const listPriceValue = options?.isShowOnRequest
      ? (
        t('productCard.onRequest')
      )
      : <FormatPrice currencyUnit={currencyUnit} price={lp} />

    if (options.isHideListPrice) {
      return null
    }

    return (
      <>
        <div className="price-box__listing-price__title">
          {t('productDetails.priceBox.listingPrice')}
        </div>
        <div className="price-box__listing-price__price">{listPriceValue}</div>
      </>
    )
  }

  const renderDeliveryDate = (dates) => {
    if (!dates || dates.length === 0) {
      return <span>{t('productDetails.priceBox.unavailableDeliveryDate')}</span>
    }
    // Find first minimum greater quantity (from server) than the user selected quantity
    const firstDateIndex = dates.findIndex((date) => quantity <= date.quantity)
    return (
      dates
        // If cannot find any (user selected quantity > max quantity in dates array from server)
        // => Show all delivery dates
        .slice(0, firstDateIndex + 1 > 0 ? firstDateIndex + 1 : dates.length)
        .map((date) => {
          const deliveryDate = formatDate(
            date.deliveryDate,
            getFormatType().default,
          )
          return deliveryDate
            ? (
              <B2becTranslation
                key={date.deliveryDate}
                value="productDetails.priceBox.deliveryInfo"
                variables={{
                  deliveryDate,
                  count: date.quantity,
                }}
              />
            )
            : (
              <span key={date.deliveryDate}>
                {t('productDetails.priceBox.unavailableDeliveryDate')}
              </span>
            )
        })
    )
  }

  const renderWarningText = (text) => {
    return (
      <p className="mb-2">
        <InfoIcon className="price-box__dangerous-goods__icon" />
        {text}
      </p>
    )
  }

  const renderAvailableState = () => {
    const isPriceEqualZero = +price === 0

    const isShowEnquiryButton = !shouldDisplayAddToCart
      || isPriceEqualZero
      || requiresInstruction
      || !hasAddProductToCartPermission

    const isHideNetPrice = !shouldDisplayAddToCart
      || isPriceEqualZero
      || !hasViewIndividualPricePermission

    const isHideListPrice = price >= listingPrice

    return (
      <>
        <div className="price-box__top-wrapper">
          <div className="price-box__top">
            <div className="price-box__title fontBold">
              {isLoading ? <Skeleton height={20} width="50%" /> : (
                t('productDetails.priceBox.title')
              )}
            </div>
            <div className="price-box__price">
              {isLoading ? <Skeleton height={20} width={100} /> : isHideNetPrice
                ? (
                  t('productCard.onRequest')
                )
                : <FormatPrice currencyUnit={currencyUnit} price={price} />}
            </div>
            {shouldHideListPriceGlobally ? null : (
              <div className="price-box__listing-price">
                {isLoading ? <Skeleton height={30} /> : (
                  renderListingPrice(listingPrice, {
                    isHideListPrice,
                    isShowOnRequest: !shouldDisplayListPrice,
                  })
                )}
              </div>
            )}
            {renderRentalInfo()}
            <div className="price-box__delivery-info">
              {isLoading ? <Skeleton height={20} /> : (
                <>
                  {hideEstimatedDeliveryTime ? null : renderDeliveryDate(deliveryDates)}
                  {t('productDetails.priceBox.shippingCostsInfo')}
                </>
              )}
            </div>
            <div className="price-box__dangerous-goods">
              {isLoading ? <Skeleton height={20} /> : (
                <>
                  {isDangerous
                    && renderWarningText(
                      t('productDetails.priceBox.thisProductHasDangerousGoods'),
                    )}
                  {showRequiresInstructionInfo
                    && isBProgramProduct
                    && renderWarningText(
                      t(
                        'productDetails.priceBox.thisProductRequiresInstruction',
                      ),
                    )}
                </>
              )}
            </div>
            <div className="price-box__dangerous-goods">
              {isLoading ? <Skeleton height={20} /> : (
                hasMinQuantity
                && renderWarningText(t('productDetails.priceBox.moqHint'))
              )}
            </div>
            <div className="price-box__item">
              {isLoading
                ? <Skeleton height={20} />
                : <span>{t('productDetails.priceBox.itemQuantity')}</span>}
            </div>
            <div className="price-box__quantity">
              {isLoading ? <Skeleton height={40} width={120} /> : (
                <B2becQuantity
                  buttonClassName="price-box__change-button"
                  initialQuantity={1}
                  inputClassName="price-box__input"
                  isPriceEqualZero={isPriceEqualZero}
                  onChange={onChangeQuantity}
                />
              )}
            </div>
          </div>
        </div>
        <div className="price-box__bottom-wrapper">
          {isLoading ? <Skeleton height={50} /> : (
            renderAddToCartButton({
              isShowEnquiryButton,
              shouldDisplayAddToCart: shouldDisplayAddToCart && hasAddProductToCartPermission,
              shouldDisableEnquiry: !hasCreateProductServicePermission,
            })
          )}
          {isRentableLoading === ASYNC_STATUS.PENDING ? <Skeleton height={50} /> : (
            renderRentableButton()
          )}
        </div>
      </>
    )
  }

  const renderUnavailableState = () => (
    <div className="price-box--unavailable">
      <p className="price-box--unavailable__message">
        This product is no longer available.
      </p>
      <CustomButton
        className="price-box__bottom__button"
        onClick={onFindAlternatives}
      >
        Find Alternatives
      </CustomButton>
    </div>
  )

  return (
    <div className="price-box__wrapper">
      {isAvailable ? renderAvailableState() : renderUnavailableState()}
      <EnquiryModal />
    </div>
  )
}

export default PriceBox
